export interface RegionSeriesProps {
  id?: number;
  slug?: string;
  region_name?: string;
  event_start_date?: string;
}

/**
 * `event/region/series` mengembalikan SELURUH musim sekaligus, jadi satu kota
 * bisa muncul lebih dari sekali — di produksi Bandung ada dua (id 46 untuk
 * musim 2025 dan id 55 untuk 2026), begitu juga Semarang (34 dan 67).
 *
 * Pakai `.find()` biasa berarti pemenangnya ditentukan urutan dari API, dan
 * untuk dua kota itu entri musim lalu kebetulan lebih dulu — halaman jadi
 * memuat jadwal 2025.
 *
 * Kalau `currentSeries` (`event/current-series`) dikasih, dia yang jadi acuan
 * musim berjalan dan fungsi ini balikin `undefined` untuk kota yang belum
 * punya event musim ini. Tanpa argumen itu perilakunya balik ke cara lama:
 * ambil `event_start_date` terbaru yang ada di region/series.
 *
 * Kota dicocokkan lewat `slug` maupun `region_name` karena pemanggilnya
 * campur — sebagian membandingkan slug, sebagian nama region.
 */
export const findCurrentRegion = (
  eventSeries: RegionSeriesProps[] = [],
  city?: string,
  currentSeries?: RegionSeriesProps[],
): RegionSeriesProps | undefined => {
  const target = String(city ?? "").toLowerCase();

  if (!target) return undefined;

  const matchCity = (list: RegionSeriesProps[] = []) =>
    (list || [])
      .filter((item) =>
        [item?.slug, item?.region_name].some(
          (value) => String(value ?? "").toLowerCase() === target,
        ),
      )
      .sort((a, b) =>
        String(b?.event_start_date ?? "").localeCompare(
          String(a?.event_start_date ?? ""),
        ),
      );

  // Kalau daftar musim berjalan (`event/current-series`) dikasih, dia yang
  // menentukan — bukan "tanggal paling baru di region/series".
  //
  // Dua endpoint ini ga sinkron: region/series cuma ngirim region yang udah
  // punya series, jadi musim berjalan yang jadwalnya belum dibuat ga muncul di
  // sana. Contohnya Tangerang — region/series cuma punya id 42 (2025),
  // padahal current-series udah punya id 74 (2026-10-23). Kalau ngandelin
  // region/series doang, yang kepilih entri 2025 dan halaman nampilin jadwal
  // musim lalu, bukan empty state.
  //
  // Tahunnya sengaja ga dihitung dari `new Date().getFullYear()`: ada musim
  // yang mulainya nyebrang tahun (Medan & Pontianak mulai 2027-01-16), jadi
  // patokan tahun kalender bakal salah.
  if (currentSeries && currentSeries.length > 0) {
    const current = matchCity(currentSeries)[0];

    // Kota ga ada di musim berjalan -> emang belum ada eventnya. Balikin
    // undefined biar pemanggilnya nampilin empty state, bukan data musim lalu.
    if (!current) return undefined;

    const detail = matchCity(eventSeries).find(
      (item) => item?.id === current?.id,
    );

    return { ...current, ...(detail || {}) };
  }

  return matchCity(eventSeries)[0];
};
